import { useCallback, useEffect, useRef, useState } from "react";
import { useOptionalNotificationContext } from "@/components/notification-provider";

const DWELL_ACKNOWLEDGEMENT_MS = 5_000;

/**
 * Keeps read acknowledgement tied to either a real interaction with one
 * conversation or an uninterrupted five-second dwell on its unread view. The
 * session ID comparison resets the signal synchronously during a thread
 * switch, before effects for the newly selected feed run. Refs keep unrelated
 * notification context updates from restarting the dwell.
 */
export function useConversationReadEngagement(sessionId: string) {
  const notifications = useOptionalNotificationContext();
  const engagedSessionIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef(sessionId);
  const [, setEngagementVersion] = useState(0);
  sessionIdRef.current = sessionId;

  const hasUserInteracted = engagedSessionIdRef.current === sessionId;

  const acknowledgeEngagement = useCallback((engagedSessionId: string) => {
    if (engagedSessionIdRef.current === engagedSessionId) return;

    engagedSessionIdRef.current = engagedSessionId;
    setEngagementVersion((version) => version + 1);
  }, []);
  const acknowledgeEngagementRef = useRef(acknowledgeEngagement);
  acknowledgeEngagementRef.current = acknowledgeEngagement;

  const markUserInteraction = useCallback((event: Event) => {
    // Synthetic/programmatic DOM events must not acknowledge unread content.
    if (!event.isTrusted) return;

    acknowledgeEngagement(sessionId);
  }, [acknowledgeEngagement, sessionId]);

  const hasUnread = notifications?.notificationState.get(sessionId)?.hasUnread === true;
  const hasAnythingUnread = hasUnread || (notifications?.inbox.notifications ?? []).some(
    (row) => row.sessionId === sessionId && row.readAt === null && row.dismissedAt === null,
  );
  const hasAnythingUnreadRef = useRef(hasAnythingUnread);
  hasAnythingUnreadRef.current = hasAnythingUnread;

  useEffect(() => {
    const capturedSessionId = sessionId;
    let dwellTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const ownsSession = () => (
      !cancelled && sessionIdRef.current === capturedSessionId
    );
    const clearDwellTimer = () => {
      if (dwellTimer === undefined) return;
      clearTimeout(dwellTimer);
      dwellTimer = undefined;
    };
    const startDwellTimer = () => {
      if (!ownsSession() || document.hidden) return;
      clearDwellTimer();
      dwellTimer = setTimeout(() => {
        dwellTimer = undefined;
        if (!ownsSession() || document.hidden || !hasAnythingUnreadRef.current) return;
        acknowledgeEngagementRef.current(capturedSessionId);
      }, DWELL_ACKNOWLEDGEMENT_MS);
    };
    const handleVisibilityChange = () => {
      clearDwellTimer();
      if (!document.hidden) startDwellTimer();
    };

    if (!hasAnythingUnread || hasUserInteracted) return undefined;

    document.addEventListener("visibilitychange", handleVisibilityChange);
    startDwellTimer();
    return () => {
      cancelled = true;
      clearDwellTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hasAnythingUnread, hasUserInteracted, sessionId]);

  return {
    hasUserInteracted,
    markUserInteraction,
  };
}
